import type { ReactNode } from 'react';
import { CALENDAR_SOURCE_LABELS, describeWorkingDays, formatClockRange } from '@nest-recruit/shared';
import { Field } from '../../../components/Field';
import { Select } from '../../../components/Select';
import { useToasts } from '../../../stores/toasts';
import { useAssignUserCalendar, useCalendars, useResolvedCalendar } from '../api/calendars-api';
import type { UserDetail } from '../api/users-api';
import styles from './WorkCalendarOverride.module.css';

/**
 * The per-user calendar override, on USER-02. D14: "HR staff working UK hours get
 * a per-user override, used only when they are the responsible party."
 *
 * On the person's record rather than on ADMIN-10, because that is where somebody
 * looks: the question is "which hours does *this person* work", not "who is on
 * this calendar". ADMIN-10 owns the calendars; this owns one person's exception
 * to them.
 *
 * Note what the override does **not** do. It is not a display timezone — the
 * `Timezone` fact above it is that, and it decides what times this person sees.
 * This decides which working week their SLA clocks run on when a task is sitting
 * with them. Two different things that sound like one, so both are labelled.
 */

interface WorkCalendarOverrideProps {
  readonly user: UserDetail;
  readonly canEdit: boolean;
}

const INHERIT = 'inherit';

export function WorkCalendarOverride({ user, canEdit }: WorkCalendarOverrideProps): ReactNode {
  const calendars = useCalendars();
  const assign = useAssignUserCalendar();
  const resolved = useResolvedCalendar({ userId: user.id });
  const showToast = useToasts((state) => state.show);

  const options = [
    { value: INHERIT, label: 'Their department’s calendar', description: 'The usual case' },
    ...(calendars.data ?? []).map((calendar) => ({
      value: calendar.id,
      label: calendar.name,
      description: `${describeWorkingDays(calendar.workingDays)} · ${formatClockRange(calendar.startTime, calendar.endTime)} · ${calendar.timezone}`,
    })),
  ];

  const change = (value: string): void => {
    const workCalendarId = value === INHERIT ? null : value;
    assign.mutate(
      { userId: user.id, workCalendarId, version: user.version },
      {
        onSuccess: () =>
          showToast({
            tone: 'success',
            message:
              workCalendarId === null
                ? `${user.fullName} follows their department’s hours again`
                : `${user.fullName}’s clocks now run on that calendar`,
          }),
        onError: (error) => showToast({ tone: 'danger', message: error.message }),
      },
    );
  };

  return (
    <div className={styles.block}>
      <Field
        label="Working hours"
        hint="Which working week their SLA clocks run on while a task is with them. This is not their display timezone."
      >
        {({ inputId, describedBy }) => (
          <Select
            id={inputId}
            describedBy={describedBy}
            label="Working hours"
            disabled={!canEdit || assign.isPending}
            value={user.workCalendarId ?? INHERIT}
            options={options}
            onChange={change}
          />
        )}
      </Field>

      {resolved.data ? (
        <p className={styles.resolved}>
          In force: <strong>{resolved.data.calendar.name}</strong> —{' '}
          {describeWorkingDays(resolved.data.calendar.workingDays)},{' '}
          {formatClockRange(resolved.data.calendar.startTime, resolved.data.calendar.endTime)}{' '}
          {resolved.data.calendar.timezone} ({CALENDAR_SOURCE_LABELS[resolved.data.source]})
        </p>
      ) : null}
    </div>
  );
}
